import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createStudent,
  deleteStudent,
  fetchAssessmentsByStudent,
  fetchStudentById,
  fetchStudents,
  updateStudent,
  type StudentPayload
} from "@/services/repositories/student-repository";
import {
  fetchAssessments as fetchAllAssessments,
  fetchAssessmentById,
  fetchAssessmentEntries,
  fetchAssessmentReport
} from "@/services/repositories/assessment-repository";
import type { Student } from "@/types/assessment";

export function useStudentsQuery() {
  return useQuery({
    queryKey: ["students"],
    queryFn: () => fetchStudents(),
    staleTime: 5 * 60 * 1000
  });
}

export function useStudentQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: ["students", studentId],
    queryFn: () => (studentId ? fetchStudentById(studentId) : Promise.resolve(undefined)),
    enabled: Boolean(studentId),
    staleTime: 5 * 60 * 1000
  });
}

export function useStudentAssessmentsQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: ["assessments", "student", studentId],
    queryFn: () => (studentId ? fetchAssessmentsByStudent(studentId) : Promise.resolve([])),
    enabled: Boolean(studentId),
    staleTime: 60 * 1000
  });
}

export function useAssessmentsQuery() {
  return useQuery({
    queryKey: ["assessments"],
    queryFn: fetchAllAssessments,
    staleTime: 60 * 1000
  });
}

export function useAssessmentQuery(assessmentId: string | undefined) {
  return useQuery({
    queryKey: ["assessments", assessmentId],
    queryFn: () => (assessmentId ? fetchAssessmentById(assessmentId) : Promise.resolve(undefined)),
    enabled: Boolean(assessmentId),
    staleTime: 60 * 1000
  });
}

export function useAssessmentReportQuery(assessmentId: string | undefined) {
  return useQuery({
    queryKey: ["assessmentReport", assessmentId],
    queryFn: () =>
      assessmentId ? fetchAssessmentReport(assessmentId) : Promise.resolve(undefined),
    enabled: Boolean(assessmentId),
    staleTime: 60 * 1000
  });
}

export function useCreateStudentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: StudentPayload) => createStudent(payload),
    onSuccess: async (created) => {
      queryClient.setQueryData<Student[] | undefined>(["students"], (previous) => {
        if (!previous) {
          return [created];
        }
        if (previous.some((student) => student.id === created.id)) {
          return previous;
        }
        return [created, ...previous];
      });
      await queryClient.invalidateQueries({ queryKey: ["students"] });
    }
  });
}

export function useUpdateStudentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, payload }: { studentId: string; payload: StudentPayload }) =>
      updateStudent(studentId, payload),
    onSuccess: async (data) => {
      queryClient.setQueryData<Student[] | undefined>(["students"], (previous) =>
        previous?.map((student) => (student.id === data.id ? data : student)),
      );
      queryClient.setQueryData(["students", data.id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["students"] }),
        queryClient.invalidateQueries({ queryKey: ["students", data.id] })
      ]);
    }
  });
}

export function useDeleteStudentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studentId: string) => deleteStudent(studentId),
    onSuccess: async (_, studentId) => {
      queryClient.setQueryData<Student[] | undefined>(["students"], (previous) =>
        previous?.filter((student) => student.id !== studentId),
      );
      queryClient.removeQueries({ queryKey: ["students", studentId] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["students"] }),
        queryClient.invalidateQueries({ queryKey: ["assessments"] })
      ]);
    }
  });
}

export function useAssessmentEntriesQuery(assessmentId: string | undefined) {
  return useQuery({
    queryKey: ["assessmentEntries", assessmentId],
    queryFn: () => (assessmentId ? fetchAssessmentEntries(assessmentId) : Promise.resolve([])),
    enabled: Boolean(assessmentId),
    staleTime: 60 * 1000
  });
}
